<template>
  <section class="authentication-form">
    <div class="innerpage-decor"></div>
    <div>
      <h2 class="title text-center">
        <router-link to="/">
          <img :src="require('@/assets/images/efunding-logo.png')" alt="logo-brand" />
        </router-link>
      </h2>

      <div v-if="isLoading" class="card text-center p-4">
        <b-spinner variant="warning" class="mb-3 mx-auto"></b-spinner>
        <p class="text-secondary">Memuat data pengguna...</p>
      </div>

      <div v-else class="card">
        <b-alert v-if="msg" :variant="alertVariant" show dismissible>
          {{ msg }}
        </b-alert>

        <!-- Step: Kirim OTP -->
        <div v-if="step === 'init'" class="text-center">
          <div class="otp-instruction-wrapper" style="margin-bottom: 4rem;">
            <span>Klik tombol di bawah untuk mengirim OTP ke&nbsp;</span>
            <span class="email">{{ email }}</span>
          </div>
          <b-button
            @click="sendOtp"
            class="btn btn-custom btn-lg theme-color w-100"
            :disabled="isProcessing"
          >
            {{ isProcessing ? "Mengirim..." : "Kirimkan Kode Verifikasi" }}
          </b-button>
        </div>

        <!-- Step: Input OTP -->
        <div v-else-if="step === 'input'">
          <p class="text-center mb-7" style="font-size: 20px;">Verifikasi Email</p>
          <div class="otp-instruction-wrapper">
            <span>Masukkan 6 digit kode OTP yang dikirim ke&nbsp;</span>
            <span class="email">{{ email }}</span>
          </div>

          <ValidationObserver v-slot="{ handleSubmit }">
            <form class="theme-form" @submit.prevent="handleSubmit(verifyOtp)">
              <ValidationProvider name="otp" rules="required|numeric|min:6|max:6" v-slot="{ errors }">
                <div class="form-group">
                  <input
                    id="otp"
                    type="text"
                    v-model="otp"
                    maxlength="6"
                    class="form-control text-center otp-input"
                    placeholder="* * * * * * "
                    name="otp"
                    :class="{ 'is-invalid': errors.length }"
                  />
                  <small class="text-danger ml-3">{{ errors[0] }}</small>
                </div>
              </ValidationProvider>

              <div class="form-button text-center">
                <button type="submit" class="btn btn-custom btn-lg theme-color w-100" :disabled="isProcessing">
                  <span v-show="isProcessing" class="spinner-border spinner-border-sm"></span>
                  <span v-if="!isProcessing">Verifikasi Sekarang</span>
                </button>
              </div>
            </form>
          </ValidationObserver>

          <!-- Resend OTP -->
          <div class="mt-3 text-center">
            <button
              v-if="resendTimer === 0"
              @click="sendOtp"
              class="btn btn-custom btn-lg theme-color w-100"
              :disabled="isProcessing"
            >
              {{ isProcessing ? "Mengirim..." : "Kirim Ulang OTP" }}
            </button>
            <p v-else class="otp-resend text-muted mt-2">
              Kirim ulang OTP tersedia dalam {{ resendTimer }} detik
            </p>
          </div>
        </div>

        <!-- Step: Verified -->
        <div v-else-if="step === 'verified'" class="text-center p-4">
          <b-icon icon="check-circle-fill" variant="success" font-scale="2" class="mb-3"></b-icon>
          <p class="text-success">Email berhasil diverifikasi.</p>
          <router-link to="/" class="btn btn-custom btn-lg theme-color w-100">Halaman Awal</router-link>
        </div>
        <!-- Step: Blocked -->
        <div v-else-if="step === 'blocked'" class="text-center p-4">
          <b-icon icon="exclamation-triangle-fill" variant="danger" font-scale="2" class="mb-3"></b-icon>
          <p class="text-danger">
            Anda telah memasukkan OTP yang salah sebanyak 5 kali. <br>Demi keamanan, verifikasi diblokir sementara.
          </p>
          <p class="otp-resend text-muted mt-2">
            Silakan coba kembali dalam {{ blockTimer }} detik.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import EmailVerification from '@/api/auth/EmailVerification';
import Authentication from '@/api/auth/authentication';

export default {
  name: 'EmailVerified',
  data() {
    return {
      email: '',
      otp: '',
      msg: '',
      alertVariant: 'info',
      isLoading: true,
      isProcessing: false,
      step: 'loading', // loading | init | input | verified
      resendTimer: 0,
      resendInterval: null,
      attemptCount: 0,
      blockUntil: null,
      blockTimer: 0,
      blockInterval: null,
    };
  },
  mounted() {
    const savedAttempts = localStorage.getItem('otp_attempt_count');
    if (savedAttempts) {
      this.attemptCount = parseInt(savedAttempts);
    }
    const blockTime = localStorage.getItem('otp_block_until');
    if (blockTime) {
      const blockUntil = parseInt(blockTime);
      const now = Date.now();
      if (now < blockUntil) {
        this.blockUntil = blockUntil;
        this.step = 'blocked';
        this.startBlockCountdown();
      }
    }

    // Cek jika sebelumnya sedang di tahap input OTP
    const savedStep = localStorage.getItem('email_verification_step');
    const savedTimestamp = localStorage.getItem('email_verification_timer');
    const savedEmail = localStorage.getItem('email_verification_email');

    if (savedStep === 'input' && savedEmail && savedTimestamp) {
      this.email = savedEmail;

      const elapsed = Math.floor((Date.now() - parseInt(savedTimestamp)) / 1000);
      const remaining = 60 - elapsed;

      if (this.step !== 'blocked') this.step = 'input';

      if (remaining > 0) {
        this.resendTimer = remaining;
        this.startResendCountdown();
      }

      this.isLoading = false;
    } else if (!this.blockUntil) {
      this.loadUser();
    }
  },
  beforeDestroy() {
    if (this.resendInterval) clearInterval(this.resendInterval);
    if (this.blockInterval) clearInterval(this.blockInterval);
  },
  beforeRouteLeave(to, from, next) {
    this.resetVerificationState(); // optional jika kamu ingin clear saat keluar halaman
    next();
  },
  methods: {
    async loadUser() {
      this.isLoading = true;
      try {
        const res = await Authentication.getMe();
        if (res.data.meta.code === 200 && res.data.data.email) {
          this.email = res.data.data.email;
          this.step = 'init';
        } else {
          this.handleError('Gagal mengambil email pengguna. Silakan login kembali.');
          localStorage.removeItem('user');
          this.$router.push('/login');
        }
      } catch (err) {
        this.handleError('Gagal memuat data pengguna.');
        localStorage.removeItem('user');
        this.$router.push('/login');
      } finally {
        this.isLoading = false;
      }
    },
    async sendOtp() {
      this.isProcessing = true;
      try {
        const res = await EmailVerification.send();
        if (res.data.meta.code === 200) {
          this.handleMessage(res.data.meta.message || 'OTP telah dikirim ke email Anda.', 'success');
          this.step = 'input';

          // Simpan state ke localStorage
          localStorage.setItem('email_verification_step', 'input');
          localStorage.setItem('email_verification_email', this.email);
          localStorage.setItem('email_verification_timer', Date.now().toString());

          this.startResendCountdown();
        } else {
          const msg = res.data.meta.message || 'Gagal mengirim OTP.';
          this.handleError(msg);
        }
      } catch (err) {
        this.handleError('Terjadi kesalahan saat mengirim OTP.');
      } finally {
        this.isProcessing = false;
      }
    },
    startResendCountdown() {
      if (this.resendInterval) clearInterval(this.resendInterval);

      this.resendInterval = setInterval(() => {
        const timestamp = localStorage.getItem('email_verification_timer');
        if (!timestamp) {
          this.resendTimer = 0;
          clearInterval(this.resendInterval);
          return;
        }

        const elapsed = Math.floor((Date.now() - parseInt(timestamp)) / 1000);
        const remaining = 60 - elapsed;

        if (remaining > 0) {
          this.resendTimer = remaining;
        } else {
          this.resendTimer = 0;
          clearInterval(this.resendInterval);
          localStorage.removeItem('email_verification_timer');
        }
      }, 1000);
    },
    startBlockCountdown() {
      if (this.blockInterval) clearInterval(this.blockInterval);
      this.blockInterval = setInterval(() => {
        const remaining = Math.floor((this.blockUntil - Date.now()) / 1000);
        if (remaining > 0) {
          this.blockTimer = remaining;
        } else {
          this.blockTimer = 0;
          this.step = 'input';
          this.attemptCount = 0;
          this.blockUntil = null;
          localStorage.removeItem('otp_block_until');
          clearInterval(this.blockInterval);
        }
      }, 1000);
    },

    resetVerificationState() {
      localStorage.removeItem('email_verification_step');
      localStorage.removeItem('email_verification_timer');
      localStorage.removeItem('email_verification_email');
      localStorage.removeItem('otp_block_until');
      localStorage.removeItem('otp_attempt_count');
      this.attemptCount = 0;
      this.blockUntil = null;
    },
    async verifyOtp() {
      if (this.otp.length !== 6 || !/^\d{6}$/.test(this.otp)) {
        this.handleError('Kode OTP harus 6 digit angka.');
        return;
      }

      // Cek apakah diblok
      if (this.blockUntil && Date.now() < this.blockUntil) {
        this.step = 'blocked';
        return;
      }

      this.isProcessing = true;
      try {
        const res = await EmailVerification.verify({ otp: this.otp });
        if (res.data.meta.code === 200) {
          this.handleMessage('Verifikasi Berhasil.', 'success');
          this.step = 'verified';

          // Reset semua
          this.resetVerificationState();
        } else {
          this.attemptCount++;
          localStorage.setItem('otp_attempt_count', this.attemptCount.toString());

          if (this.attemptCount >= 5) {
            const blockTime = Date.now() + 5 * 60 * 1000; // 5 menit
            this.blockUntil = blockTime;
            localStorage.setItem('otp_block_until', blockTime.toString());
            this.step = 'blocked';
            this.startBlockCountdown();
          }
          const msg = res.data.meta.message || 'OTP tidak valid atau kadaluarsa.';
          this.handleError(msg);
        }
      } catch (err) {
        this.handleError('Gagal memverifikasi email.');
      } finally {
        this.isProcessing = false;
      }
    },
    handleMessage(message, variant) {
      this.msg = message;
      this.alertVariant = variant || 'info';
    },
    handleError(message) {
      this.msg = message;
      this.alertVariant = 'danger';
    }
  }
};
</script>

<style scoped>

.card {
  max-width: 500px;
  margin: 0 auto;
  padding: 1.5rem;
  word-break: normal;
}

.text-center p {
  white-space: normal;
  word-break: keep-all;
}
.authentication-form .brand-logo {
  max-width: 180px;
}
.otp-input {
  font-size: 1rem;
  letter-spacing: 0.3rem;
  text-align: center;
  width: 100%;
  max-width: 200px;
  margin: 0 auto;
  height: auto;
}
.text-muted.small {
  font-size: 0.85rem;
}
.is-invalid {
  border-color: #dc3545;
}
.theme-form {
  margin-top: 1rem;
}

.otp-input::placeholder {
  font-size: 0.9rem;
  color: #ccc;
}

.otp-instruction-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  font-size: 0.9rem;
  text-align: center;
  color: #6c757d;
}

.otp-instruction-wrapper .email {
  font-weight: 600;
  color: #343a40;
  white-space: normal;
  word-break: break-word;
}

.otp-resend {
  font-size: 0.85rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media (max-width: 375px) {
  .otp-instruction,
  .otp-resend {
    white-space: normal;
    text-overflow: unset;
  }
}
@media (max-width: 768px) {
  .card {
    padding: 1rem !important;
    max-width: 70% !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/public/auth/waitingVerifiedEmail.vue